import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import api from "@/lib/api";

/** Zeigt die konfigurierten Servicegebiete als farbig markierte Kreise auf einer Mapbox-Karte.
 * Auto-fit auf die Bounds der Punkte. Fällt still auf null zurück, wenn keine Punkte oder kein Token.
 */
export default function ServiceAreaMap({ slug, primaryColor = "#2A86A8", secondaryColor = "#3FA64E" }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [data, setData] = useState(null);
  const [token, setToken] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [geoRes, cfgRes] = await Promise.all([
          api.get(`/portal/${slug}/service-area-geo`),
          api.get("/mapbox/config"),
        ]);
        if (!alive) return;
        setData(geoRes.data);
        setToken(cfgRes.data?.public_token || null);
      } catch (_e) {
        if (alive) setData({ points: [] });
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (!token || !data || !data.points?.length || !containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = token;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/light-v11",
      center: [data.points[0].lng, data.points[0].lat],
      zoom: 9,
      attributionControl: false,
      cooperativeGestures: true,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new mapboxgl.AttributionControl({ compact: true }));
    map.on("load", () => {
      const features = data.points.map((p) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [p.lng, p.lat] },
        properties: { zip: p.zip, city: p.city, label: [p.zip, p.city].filter(Boolean).join(" · ") },
      }));
      map.addSource("service-areas", { type: "geojson", data: { type: "FeatureCollection", features } });
      // Weiche Fläche (großer Blur-Kreis)
      map.addLayer({
        id: "service-areas-fill",
        type: "circle",
        source: "service-areas",
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 6, 30, 10, 70, 14, 130],
          "circle-color": primaryColor,
          "circle-opacity": 0.18,
          "circle-blur": 0.6,
        },
      });
      // Klarer Kern-Punkt
      map.addLayer({
        id: "service-areas-core",
        type: "circle",
        source: "service-areas",
        paint: {
          "circle-radius": 8,
          "circle-color": primaryColor,
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2,
        },
      });
      // Labels
      map.addLayer({
        id: "service-areas-label",
        type: "symbol",
        source: "service-areas",
        layout: {
          "text-field": ["get", "label"],
          "text-size": 11,
          "text-offset": [0, 1.4],
          "text-anchor": "top",
          "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"],
        },
        paint: {
          "text-color": "#111827",
          "text-halo-color": "#ffffff",
          "text-halo-width": 1.5,
        },
      });
      // Bounds
      if (data.points.length === 1) {
        map.jumpTo({ center: [data.points[0].lng, data.points[0].lat], zoom: 11 });
      } else {
        const b = new mapboxgl.LngLatBounds();
        data.points.forEach((p) => b.extend([p.lng, p.lat]));
        map.fitBounds(b, { padding: 60, maxZoom: 12, duration: 0 });
      }
      // Popup on hover
      const popup = new mapboxgl.Popup({ closeButton: false, offset: 12 });
      map.on("mouseenter", "service-areas-core", (e) => {
        map.getCanvas().style.cursor = "pointer";
        const f = e.features?.[0];
        if (!f) return;
        popup.setLngLat(f.geometry.coordinates)
          .setHTML(`<div style="font-family:system-ui;font-size:12px"><strong>${f.properties.city || "PLZ"} ${f.properties.zip}</strong></div>`)
          .addTo(map);
      });
      map.on("mouseleave", "service-areas-core", () => {
        map.getCanvas().style.cursor = "";
        popup.remove();
      });
      mapRef.current = map;
      setReady(true);
    });
    return () => {
      map.remove();
      mapRef.current = null;
      setReady(false);
    };
  }, [token, data, primaryColor]);

  if (!data || !data.points?.length) return null;
  if (!token) {
    return (
      <div className="rounded-2xl border border-black/10 bg-zinc-50 p-6 text-xs text-zinc-500 text-center" data-testid="service-area-map-fallback">
        Karte derzeit nicht verfügbar. Wir bedienen: {data.points.map((p) => [p.zip, p.city].filter(Boolean).join(" ")).join(", ")}.
      </div>
    );
  }
  return (
    <div className="relative rounded-2xl overflow-hidden border border-black/10 shadow-sm bg-white" data-testid="service-area-map">
      <div ref={containerRef} className="w-full h-[380px] md:h-[460px]" />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-50/70 text-xs text-zinc-500">Karte lädt…</div>
      )}
      <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 text-[11px] font-medium border border-black/10 flex items-center gap-2 shadow-sm">
        <span className="inline-block w-3 h-3 rounded-full" style={{ background: primaryColor }} />
        <span>Unser Servicegebiet — {data.points.length} {data.points.length === 1 ? "Ort" : "Orte"}</span>
      </div>
    </div>
  );
}
